// V2 shell — sidebar, top bar, and the route back to classic.
//
// Deliberately NOT reusing components/Layout.js: V2's whole premise is that
// it carries none of classic's structural assumptions (tenant switcher,
// permission-graded nav, per-feature sections). It shares the API service
// layer and nothing else.

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { DOMAINS, findItem } from './nav';
import api, { dashboardService } from '../../services/api';
import { FLIP_DECIDED_ON, daysSince, flipGates } from '../domains/payouts/Recon';
import s from './Shell.module.css';

// ── THE STANDING REMINDER ───────────────────────────────────────────────────
//
// Founder, 24 Sep: "lets have a timer on iot admin v2 above the header itself
// so that we could remember that flip needs to be made", with a link to the
// screen that shows whether it is reconciling.
//
// THE TIMER IS NOT THE GATE. It counts elapsed days so the decision cannot be
// quietly forgotten; readiness is the PREDICATE next to it (flipGates), and the
// bar only goes green when all three are met. "It has been N days, flip it" is
// a date standing in for a predicate, which is precisely what the loss-card
// clamp was removed for.
//
// Fetched ONCE per app load and memoised at module scope: this bar renders on
// every V2 screen and must not put a request on each of them. If the fetch
// fails the bar still renders, without the state — a reminder that disappears
// when the server hiccups is not a reminder.
let flipPromise = null;
const fetchFlip = () => {
  if (!flipPromise) {
    flipPromise = api.get('/revenue/admin/recon')
      .then((r) => (r?.data && typeof r.data === 'object' ? r.data : null))
      .catch(() => null);
  }
  return flipPromise;
};

export default function Shell({ children }) {
  const { pathname } = useLocation();
  const current = findItem(pathname);
  const [alerts, setAlerts] = useState(null);
  const [recon, setRecon] = useState(undefined);   // undefined = still asking

  // The admission-queue count is the one number that belongs in the chrome:
  // it is the console's only standing "a human is required" signal. Test rows
  // are excluded so the badge matches what the Admission screen shows by
  // default — a badge that disagrees with the page it links to is worse than
  // no badge.
  useEffect(() => {
    let alive = true;
    dashboardService.getAlerts()
      .then((r) => {
        if (!alive) return;
        const rows = Array.isArray(r?.data) ? r.data : null;
        setAlerts(rows ? rows.filter((x) => !x.is_test_device).length : null);
      })
      .catch(() => { if (alive) setAlerts(null); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchFlip().then((d) => { if (alive) setRecon(d); });
    return () => { alive = false; };
  }, []);

  const badgeFor = (item) => (item.badge === 'alerts' && alerts ? alerts : null);

  return (
    <div className={s.root}>
      <div className={s.brand}>
        <span className={s.mark}>gg</span>
        <span className={s.wordmark}>iot-admin</span>
        <span className={s.v2tag}>V2</span>
      </div>

      {/* No global search in the chrome: entity search is half-built and
          promising it in the header before it covers every entity would be a
          promise the console cannot keep. ⌘K still opens the palette for
          anyone who wants it; it is simply not advertised yet. */}
      <header className={s.top}>
        <nav className={s.crumbs} aria-label="Breadcrumb">
          <span>{DOMAINS.find((d) => d.id === current?.domain)?.label || 'Console'}</span>
          {current && <><span className={s.crumbSep} aria-hidden="true">/</span><span className={s.crumbNow}>{current.label}</span></>}
        </nav>
        <div className={s.spacer} />
        <span className={s.who}><span className={s.whoDot} aria-hidden="true" />Super admin</span>
      </header>

      {/* NAV ORDER AND CLICKABILITY, fixed 12 Sep.
          Measured before changing anything: 33 items, 14 of them stubs, and
          FIFTEEN with no route at all in V2App — so a third of this nav led to a
          blank page. Two groups (Audience, Ops) had zero live screens between
          them, interleaved with five that work.

          So: a stub is no longer a Link. It renders as a dimmed, unclickable row
          with its SOON chip — Brands Manager's own rule for an unbuilt tool
          (Sidebar.jsx: `soon: true` items are "dimmed and non-clickable"). A nav
          that looks identical whether or not a screen exists is the defect.

          And groups flagged `pending` (every item a stub) sort to the foot,
          rather than being deleted: the roadmap is worth seeing, it just should
          not sit above working screens. */}
      <aside className={s.nav}>
        {[...DOMAINS].sort((a, b) => (a.pending ? 1 : 0) - (b.pending ? 1 : 0)).map((d) => (
          <div key={d.id} className={s.group}>
            <div className={s.groupHead}>{d.label}{d.pending ? ' · soon' : ''}</div>
            {d.items.filter((item) => !item.hidden).map((item) => {
              const on = current?.id === item.id;
              const badge = badgeFor(item);
              const chip = badge ? <span className={s.count}>{badge}</span>
                : item.status === 'stub' ? <span className={s.stub}>SOON</span> : null;

              // Unbuilt: not a link. aria-disabled rather than a disabled
              // attribute, because this is not a control — it is a signpost to
              // somewhere that does not exist yet.
              if (item.status === 'stub') {
                return (
                  <span key={item.id} className={`${s.link} ${s.linkSoon}`} aria-disabled="true">
                    <span className={s.linkLabel}>{item.label}</span>
                    {chip}
                  </span>
                );
              }
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`${s.link} ${on ? s.linkOn : ''}`}
                  aria-current={on ? 'page' : undefined}
                >
                  <span className={s.linkLabel}>{item.label}</span>
                  {chip}
                </Link>
              );
            })}
          </div>
        ))}
      </aside>

      <main className={s.main}>
        {(() => {
          const gates = flipGates(recon?.flip);
          const met = gates.filter((g) => g.met).length;
          const ready = met === gates.length;
          const elapsed = daysSince(FLIP_DECIDED_ON);
          const breaches = Array.isArray(recon?.checks)
            ? recon.checks.filter((c) => c.state === 'breach').length : 0;
          return (
            <div className={`${s.flipBar} ${ready ? s.flipBarReady : ''}`}>
              <span className={s.flipDot} aria-hidden="true" />
              <span>
                <b>Money cell</b>{elapsed == null ? '' : ` · day ${elapsed}`}
                {recon === undefined ? ' · checking…' : ` · ${met} of ${gates.length} gates met`}
              </span>
              <span className={s.flipMuted}>
                {ready
                  ? 'All gates met — the read may be flipped from raw batches to the cell.'
                  : `Waiting: ${gates.find((g) => !g.met)?.pending}.`}
              </span>
              <span className={s.stripSpacer} />
              {breaches > 0 && (
                <span className={s.flipBreach}>{breaches} recon check{breaches === 1 ? '' : 's'} breached</span>
              )}
              <Link className={s.stripLink} to="/v2/payouts/recon">See the reconciliation</Link>
            </div>
          );
        })()}
        <div className={s.strip}>
          <ArrowLeft size={13} aria-hidden="true" />
          <span>You’re in the new console.</span>
          <a className={s.stripLink} href="/dashboard">Back to classic iot-admin</a>
          <span className={s.stripSpacer} />
          <span className={s.stripMuted}>Classic stays available and unchanged.</span>
        </div>
        <div className={s.inner}>{children}</div>
      </main>
    </div>
  );
}
